import {useEffect} from "react";
import {useFormContext} from "react-hook-form";

import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {SetupInfo} from "@/pages/integrations/edit/subForms/components/SetupInfo.tsx";
import {IntegrationModuleInfo, IntegrationModules} from "@/pages/integrations/integrations.types.ts";

export type FormValuesKey = keyof IntegrationFormData;

interface DataDogFormProps {
  serverErrors?: ServerErrors;
}

export const DataDogForm = ({serverErrors}: DataDogFormProps) => {
  const {setError, getValues, setValue} = useFormContext();

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => setError(`settings.${key}` as FormValuesKey, {message: serverErrors[key][0]}));
    }
  }, [setError, serverErrors]);

  // Integrations saved before this setting existed have no key in settings;
  // missing must render as checked to match the backend's default-true behavior.
  // TODO(TRUST-1145): remove once the backend merges module default settings
  // into stored/served settings, making absent keys impossible on the read path
  useEffect(() => {
    if (getValues("settings.prepend_check_tags") === undefined) {
      setValue("settings.prepend_check_tags", true);
    }
  }, [getValues, setValue]);

  return (
    <>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField name="settings.api_key" label="API Key" required={true} maxLength={32} autoFocus={true} />
        </div>
        <div className="col-6 mb-3">
          <InputField name="settings.app_key" label="App Key" required={true} maxLength={40} />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <SelectField
            name="settings.region"
            label="API Region"
            required={false}
            options={[
              {value: "US", label: "US1 - app.datadoghq.com"},
              {value: "US3", label: "US3 - us3.datadoghq.com"},
              {value: "US5", label: "US5 - us5.datadoghq.com"},
              {value: "EU", label: "EU1 - app.datadoghq.eu"},
              {value: "AP1", label: "AP1 - ap1.datadoghq.com"},
              {value: "AP2", label: "AP2 - ap2.datadoghq.com"},
              {value: "UK1", label: "UK1 - uk1.datadoghq.com"},
            ]}
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-12 mb-3">
          <SwitchField
            name="settings.prepend_check_tags"
            label='Prepend "uptime-tag:" to check tags'
            tooltip="When disabled, check tags are sent to Datadog unmodified."
          />
        </div>
      </div>
      <SetupInfo>
        <div className="p-2">
          Login to your Datadog account and navigate to <strong>Organization Settings &gt; API Keys</strong>. Click an
          existing key to reveal and copy it, or click <strong>New Key</strong> to create one. Then navigate to{" "}
          <strong>Organization Settings &gt; Application Keys</strong> and copy an existing key or click{" "}
          <strong>New Key</strong> to create one. Application Keys are tied to the user who created them and inherit
          that user&apos;s permissions.
        </div>
        <div className="p-2">
          Paste the <strong>API Key</strong> and the <strong>Application Key</strong> into their respective fields.
        </div>
        <div className="p-2">
          <a href="https://support.uptime.com/hc/en-us/articles/115002536629-Configuring-Datadog" target="_blank">
            View {IntegrationModuleInfo[IntegrationModules.DATADOG].label} Support Article &rarr;
          </a>
        </div>
      </SetupInfo>
    </>
  );
};
